"use client";

import { useEffect, useState, type ReactNode } from "react";
import { Download, ExternalLink } from "lucide-react";
import type { Candidate } from "@/lib/offers";
import { isHidden, loadStore, saveStore } from "@/lib/offers/client-store";
import { OFFERS_ENABLED } from "@/lib/offers/enabled";

const MAX_SHOWN = 3;

type Fallback = { href: string; name: string; iconUrl?: string; blurb: string };

// Reward-link grid with up to three approved sponsored offers mixed in. The reward
// cards arrive server-rendered as `items`; offers load after the page (so they
// never slow it down) and are placed:
//  - mobile (one column): up to three, at random spots between the reward
//    cards, never before the first one
//  - sm and up: only the best-paying offer, as one wide card above the list
// Used on the overview, free-reward and promo-code pages of every game; with
// an empty list the offers simply stack where the list would be.
// Order is shuffled per page view, weighted by payout. An offer is hidden for
// 30 days after a click or "Not interested", and games whose page this
// visitor has read (they probably play it) are skipped. With no offers at all
// the old "You might also like" card for one of our own games shows instead.
// Preview: add ?offertest=ios or ?offertest=android to the page URL to see
// what a US visitor gets; links are disabled in preview.
// With OFFERS_ENABLED off nothing is fetched: the reward cards render as a
// plain grid and the "You might also like" fallback shows from the first render.
export default function FeaturedOffer({
  items,
  gridClass = "grid gap-4 sm:grid-cols-2 lg:grid-cols-3",
  itemClass = "",
  placement,
  currentSlug,
  fallback,
}: {
  items: ReactNode[];
  gridClass?: string;
  itemClass?: string;
  placement: string;
  currentSlug?: string;
  fallback?: Fallback | null;
}) {
  const [offers, setOffers] = useState<Candidate[] | null>(OFFERS_ENABLED ? null : []);
  // Mobile insert position (index into items) for each offer key.
  const [slots, setSlots] = useState<Record<string, number>>({});
  const [test, setTest] = useState(false);
  const [testDevice, setTestDevice] = useState<"ios" | "android">("ios");

  useEffect(() => {
    if (!OFFERS_ENABLED) return;
    const t = new URLSearchParams(window.location.search).get("offertest");
    const qs = new URLSearchParams({ p: placement });
    if (t === "ios" || t === "android") {
      qs.set("test", t);
      setTestDevice(t);
    }
    let cancelled = false;
    fetch(`/api/featured-offer?${qs}`)
      .then((r) => (r.ok ? r.json() : { offers: [], test: false }))
      .then((d: { offers: Candidate[]; test: boolean }) => {
        if (cancelled) return;
        const s = loadStore();
        // Preview ignores this browser's history so you always see everything.
        const list = (d.offers ?? []).filter(
          (c) => d.test || (!isHidden(s, c.key) && !(c.slug && (c.slug === currentSlug || s.played.includes(c.slug)))),
        );
        const ordered = weightedShuffle(list);
        setTest(d.test);
        setOffers(ordered);
        setSlots(randomSlots(ordered, items.length));
      })
      .catch(() => !cancelled && setOffers([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement]);

  const hide = (key: string) => {
    if (test) return;
    const s = loadStore();
    s.hidden[key] = Date.now();
    saveStore(s);
  };
  const dismiss = (key: string) => {
    hide(key);
    setOffers((list) => list?.filter((c) => c.key !== key) ?? null);
  };

  const card = (o: Candidate, wide = false) => (
    <OfferCard
      wide={wide}
      offer={o}
      test={test}
      testDevice={testDevice}
      onClick={() => hide(o.key)}
      onDismiss={() => dismiss(o.key)}
    />
  );

  // At most MAX_SHOWN sponsored cards. The rest of the shuffled pool waits
  // behind them, so "Not interested" pulls the next one in.
  const list = (offers ?? []).slice(0, MAX_SHOWN);
  // sm and up: one card only, the best-paying offer left in the pool.
  const top = (offers ?? []).reduce<Candidate | null>((a, c) => (!a || c.w > a.w ? c : a), null);

  // Mobile: reward cards with offers spliced in. Offer cards are sm:hidden;
  // anything without a gap inside the list goes after the last item.
  const offerLi = (o: Candidate) => (
    <li key={`offer-${o.key}`} className={`sm:hidden ${itemClass}`}>
      {card(o)}
    </li>
  );
  const mixed: ReactNode[] = [];
  items.forEach((item, i) => {
    mixed.push(item);
    for (const o of list) if ((slots[o.key] ?? items.length) === i + 1 && i + 1 < items.length) mixed.push(offerLi(o));
  });
  for (const o of list) if ((slots[o.key] ?? items.length) >= items.length) mixed.push(offerLi(o));

  return (
    <>
      {top && <div className="mb-4 hidden sm:block">{card(top, true)}</div>}
      {mixed.length > 0 && <ul className={gridClass}>{mixed}</ul>}
      {offers !== null && list.length === 0 && fallback && (
        <a
          href={fallback.href}
          className="group mt-4 flex items-center gap-4 rounded-2xl bg-white p-5"
          style={{ border: "1.5px solid #dde1e7", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
        >
          {fallback.iconUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={fallback.iconUrl} alt="" width={56} height={56} className="h-14 w-14 shrink-0 rounded-2xl" />
          )}
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "#1877F2" }}>
              You might also like
            </p>
            <p className="mt-1 text-lg font-extrabold text-[#0f172a] group-hover:text-[#1877F2]">{fallback.name}</p>
            <p className="text-sm text-[#6b7280]">{fallback.blurb}</p>
          </div>
        </a>
      )}
    </>
  );
}

// Higher payouts tend to come first, but every order is possible.
function weightedShuffle(list: Candidate[]): Candidate[] {
  return list
    .map((c) => ({ c, r: Math.pow(Math.random(), 1 / Math.max(c.w, 0.1)) }))
    .sort((a, b) => b.r - a.r)
    .map((x) => x.c);
}

// Random distinct gaps between reward cards (1..n-1), so the first card is
// always a reward link. Extra offers fall through to the end.
function randomSlots(list: Candidate[], n: number): Record<string, number> {
  const gaps = Array.from({ length: Math.max(n - 1, 0) }, (_, i) => i + 1);
  for (let i = gaps.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [gaps[i], gaps[j]] = [gaps[j], gaps[i]];
  }
  const out: Record<string, number> = {};
  list.forEach((c, i) => (out[c.key] = gaps[i] ?? n));
  return out;
}

function OfferCard({
  wide,
  offer,
  test,
  testDevice,
  onClick,
  onDismiss,
}: {
  wide?: boolean;
  offer: Candidate;
  test: boolean;
  testDevice: "ios" | "android";
  onClick: () => void;
  onDismiss: () => void;
}) {
  const isGame = offer.kind === "game";
  const install = (
    <a
      href={test ? undefined : offer.link}
      target="_blank"
      rel="sponsored nofollow noopener"
      aria-disabled={test || undefined}
      onClick={(e) => {
        if (test) {
          e.preventDefault();
          return;
        }
        onClick();
      }}
      className={`flex w-full items-center whitespace-nowrap justify-center gap-2 rounded-xl px-5 py-3 text-base font-bold text-white transition ${test ? "cursor-not-allowed opacity-60" : "hover:opacity-90"}`}
      style={{ background: "#1877F2" }}
    >
      {isGame ? <Download className="h-4 w-4" aria-hidden /> : <ExternalLink className="h-4 w-4" aria-hidden />}
      {isGame ? "Install free" : "Learn more"}
    </a>
  );
  const footer = (
    <div className="mt-3 flex items-center justify-between gap-3 text-xs text-[#9ca3af]">
      <span>{isGame ? "Free to play. " : ""}From our advertising partner.</span>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 font-semibold text-[#6b7280] underline-offset-2 hover:text-[#1877F2] hover:underline"
      >
        Not interested
      </button>
    </div>
  );
  const preview = test && (
    <p className="px-5 py-2 text-xs font-bold text-[#92400e]" style={{ background: "#fef3c7" }}>
      Preview: US {testDevice === "android" ? "Android" : "iPhone"} offer. Link disabled.
    </p>
  );
  const badges = (
    <div className="flex items-center gap-3">
      <span className="rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: "#eff6ff", color: "#1877F2" }}>
        {isGame ? "Featured game" : "Featured survey"}
      </span>
      <span className="text-[11px] font-bold uppercase tracking-widest text-[#9ca3af]">Sponsored</span>
    </div>
  );

  // One wide row for the single desktop card: icon and text on the left,
  // install button on the right.
  if (wide) {
    return (
      <section
        aria-label="Sponsored"
        className="overflow-hidden bg-white"
        style={{ borderRadius: 20, border: "1.5px solid #dde1e7", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}
      >
        {preview}
        <div className="flex items-center gap-5 p-5">
          {offer.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={offer.image}
              alt=""
              width={80}
              height={80}
              className="h-20 w-20 shrink-0 rounded-2xl object-cover"
              style={{ border: "1px solid #eef0f3" }}
            />
          ) : null}
          <div className="min-w-0 flex-1">
            {badges}
            <p className="mt-2 text-xl font-extrabold leading-tight text-[#0f172a]" style={{ fontFamily: "var(--font-sora)" }}>
              {offer.name}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-[#4b5563]">{offer.blurb}</p>
          </div>
          <div className="w-60 shrink-0">
            {install}
            {footer}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Sponsored"
      className="flex h-full flex-col overflow-hidden bg-white"
      style={{ borderRadius: 20, border: "1.5px solid #dde1e7", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}
    >
      {preview}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-3">
          <span
            className="rounded-full px-2.5 py-1 text-xs font-bold"
            style={{ background: "#eff6ff", color: "#1877F2" }}
          >
            {isGame ? "Featured game" : "Featured survey"}
          </span>
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#9ca3af]">Sponsored</span>
        </div>

        <div className="mt-4 flex items-start gap-4">
          {offer.image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={offer.image}
              alt=""
              width={64}
              height={64}
              className="h-16 w-16 shrink-0 rounded-2xl object-cover"
              style={{ border: "1px solid #eef0f3" }}
            />
          ) : null}
          <div className="min-w-0">
            <p className="text-lg font-extrabold leading-tight text-[#0f172a]" style={{ fontFamily: "var(--font-sora)" }}>
              {offer.name}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-[#4b5563]">{offer.blurb}</p>
          </div>
        </div>

        <div className="mt-auto pt-4">{install}</div>
        {footer}
      </div>
    </section>
  );
}
